import { objectOrRelationshipTooltipContent, type CreateAvailability, type DataIndexItemRef } from './item';

/**
 * The "Use in" menu: what an item can seed, and which of those are offered.
 *
 * Ported from the page this replaces
 * (`~/code/ui/app/components/data-index-details-drawer/object-attribute-creation/index.gts`),
 * order included. Kept apart from the menu so the gating can be read and
 * tested without rendering anything.
 */

/** The three the menu makes something for. */
export type CreatableTarget = 'segment' | 'campaign' | 'newsletter';

/** The two it only points at. */
export type LinkTarget = 'snippet' | 'template';

/** The five things an item can seed, in the order the menu lists them. */
export type UseInTarget = CreatableTarget | LinkTarget;

type UseInActionBase = {
  label: string;
  icon: 'segment' | 'campaigns' | 'editor-code' | 'layout' | 'newsletter';
  isDisabled: boolean;
  /** Why it is disabled, where there is something to say. */
  tooltip: string | undefined;
};

/**
 * Split by what the row does, not by a nullable href: a caller reading
 * `target` to start a write should not have to be told the two link rows
 * cannot appear there.
 */
export type UseInAction =
  | (UseInActionBase & { target: CreatableTarget; href?: undefined })
  | (UseInActionBase & { target: LinkTarget; href: string; isExternal?: boolean });

export type UseInScope = {
  item: DataIndexItemRef;
  /** Builder plans have no segments or automations. */
  isBuilder: boolean;
  /**
   * Whether the workspace shows templates, snippets and layouts for *this*
   * item. The flag gates only the two object kinds — a profile attribute
   * or an event always shows them — which is what `withContentUsage`
   * already answers.
   */
  withTemplatesSnippetsLayouts: boolean;
  /** Answers the create permission for one of the three writes. */
  can: (target: 'segment' | 'campaign' | 'newsletter') => boolean;
  /** Where the two links point. */
  hrefs: { snippet: string; template: string };
};

/**
 * What the menu offers, in order.
 *
 * Three rules, each the page this replaces': a Builder plan has no
 * segments or automations at all; templates and snippets appear only where
 * the workspace shows content usage; and a one-time send is always
 * offered. An action whose record cannot be seeded by this kind of item is
 * listed but disabled, with the reason — the menu says what is possible
 * here rather than hiding it.
 */
export function listUseInActions(scope: UseInScope): UseInAction[] {
  const actions: UseInAction[] = [];
  const availability = (target: Parameters<typeof objectOrRelationshipTooltipContent>[1]): CreateAvailability =>
    objectOrRelationshipTooltipContent(scope.item.kind, target);

  if (!scope.isBuilder) {
    const segment = availability('segment');
    const campaign = availability('campaign');
    actions.push(
      {
        target: 'segment',
        label: 'Segment',
        icon: 'segment',
        isDisabled: segment.disabled || !scope.can('segment'),
        tooltip: segment.content,
      },
      {
        target: 'campaign',
        label: 'Automation',
        icon: 'campaigns',
        isDisabled: campaign.disabled || !scope.can('campaign'),
        tooltip: campaign.content,
      },
    );
  }

  if (scope.withTemplatesSnippetsLayouts) {
    const snippet = availability('snippet');
    const template = availability('template');
    actions.push(
      {
        target: 'snippet',
        label: 'Snippet',
        icon: 'editor-code',
        href: scope.hrefs.snippet,
        isDisabled: snippet.disabled,
        tooltip: snippet.content,
      },
      {
        target: 'template',
        label: 'Template',
        icon: 'layout',
        href: scope.hrefs.template,
        isExternal: true,
        isDisabled: template.disabled,
        tooltip: template.content,
      },
    );
  }

  const newsletter = availability('newsletter');
  actions.push({
    target: 'newsletter',
    label: 'One-time send',
    icon: 'newsletter',
    isDisabled: newsletter.disabled || !scope.can('newsletter'),
    tooltip: newsletter.content,
  });

  return actions;
}
